import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { DefaultTheme, useTheme } from "styled-components/native";
import { CheckBox, Input, VDBText } from "@components/atoms";
import { ActivityIndicator, FlatList, Keyboard, Platform } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { SelectionType } from "@components/atoms/Filters/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import {
  NavigationProp,
  useFocusEffect,
  useNavigation,
} from "@react-navigation/native";
import {
  removeFiltersPayload,
  setFiltersPayload,
  useSearchFiltersSlice,
} from "@redux/slices/searchFilters";
import { useDispatch } from "react-redux";
import { useSearchFiltersData } from "@apis/searchFilter";
import { useSelector } from "react-redux";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import useChangeProductType from "../../hooks/useChangeProductType";
import { useTranslation } from "react-i18next";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchGroupSlice } from "@redux/slices/searchGroupsSlice";
import { Controller, useForm } from "react-hook-form";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { VDBIcons } from "@utils/VDBIcons";
import { Portal } from "@gorhom/portal";
import { TextInput } from "react-native-gesture-handler";
import { checkboxPreviouslySelected } from "@components/atoms/Filters/FilterSection/Utils";
import { css } from "styled-components/native";

const flatListFlexStyle = { flex: 1, minHeight: 0 };

const FilterSearchScreen = (props: any) => {
  const { route } = props;
  const params = route && route.params ? route.params : props;
  const {
    title: label,
    customIcon,
    selectionType,
    payloadName,
    sectionName,
    api,
    server_side_search,
    value: alreadySelectedValues,
    selectedStyle,
    api_query_key,
    name,
    inputLabel,
    onClose,
    sub_options,
    brandsData: passedBrandsData,
    brandsLoading: passedBrandsLoading,
    defaultList,
  } = params;
  const selectedTheme: SelectedItemTheme = selectedStyle;
  const dispatch = useDispatch();
  const { bottom } = useSafeAreaInsets();
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp<any>>();
  const { auxPayload: filtersPayload } = useSearchFiltersSlice();
  const productType = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const theme = useTheme();
  const typography = useTypographyStore();
  const [options, setOptions] = useState<any[] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [startRangeIndex, setStartRangeIndex] = useState<number | null>(null);
  const { selectedGroups } = useSearchGroupSlice();
  const queryClient = useQueryClient();
  const inputRef = useRef<TextInput>(null);

  let mGroupIds = selectedGroups[productType] ?? [];
  let vdbSetting = false;
  if (mGroupIds.includes(0)) {
    vdbSetting = true;
    mGroupIds = mGroupIds.filter((groupId: number) => groupId !== 0);
  }

  const isEnableBrandSearch = payloadName?.includes("brands");

  const checkedValue = checkboxPreviouslySelected(
    payloadName,
    filtersPayload,
    sectionName,
    sub_options?.[0]?.options || []
  );

  const selected = useMemo(
    () =>
      sub_options?.[0]?.options?.filter((option) => {
        if (checkedValue?.includes(option?.search_payload?.[0])) {
          return checkedValue?.includes(option?.search_payload?.[0]);
        } else if (checkedValue?.includes(option?.payload_name?.[0])) {
          return checkedValue?.includes(option?.payload_name?.[0]);
        }
      }),
    [options, checkedValue]
  );

  const actualSelected =
    checkedValue === null ||
    (Array.isArray(checkedValue) && checkedValue.length === 0)
      ? []
      : selected;

  const [selectedSubItems, setSelectedSubItems] = useState<any[]>(
    actualSelected || []
  );
  const [footerBarHeight, setFooterBarHeight] = useState(0);

  const brandsData = passedBrandsData;
  const brandsLoading = passedBrandsLoading;

  const { data: searchFiltersData, isFetching: searchFiltersFetching } =
    useSearchFiltersData(
      !isEnableBrandSearch && (!server_side_search || !!searchQuery),
      api,
      !!server_side_search,
      searchQuery,
      api_query_key,
      productType,
      name,
      vdbSetting,
      mGroupIds
    );

  const data = isEnableBrandSearch ? brandsData : searchFiltersData;
  const isFetching = isEnableBrandSearch
    ? brandsLoading
    : searchFiltersFetching;

  const { control, reset } = useForm({
    defaultValues: {
      searchQueryFormValue: "",
    },
  });

  useFocusEffect(
    useCallback(() => {
      return () => {
        Keyboard.dismiss();
        queryClient.removeQueries({ queryKey: ["GET_SEARCH_FILTERS_PAYLOAD"] });
      };
    }, [queryClient])
  );

  useChangeProductType(productType);

  useEffect(() => {
    navigation.setOptions({
      headerTitle: label,
    });

    if (alreadySelectedValues) {
      setSelectedItems(alreadySelectedValues);
    }
  }, []);

  useEffect(() => {
    if (isEnableBrandSearch) {
      setOptions(data || []);
    } else if (server_side_search) {
      setOptions(data?.data || data);
    } else {
      setOptions(data?.data?.list || data?.data || data);
    }
  }, [data, isEnableBrandSearch]);

  const resetAllFilters = () => {
    if (sectionName && payloadName) {
      dispatch(removeFiltersPayload({ sectionName, payloadName }));
    }
    setSelectedItems([]);
    reset();
    setSearchQuery("");
  };

  const applyFilters = () => {
    if (selectedItems.length === 0 && sectionName && payloadName) {
      dispatch(removeFiltersPayload({ sectionName, payloadName }));
    } else {
      const payload: any = {};
      payloadName?.forEach((val: string) => {
        payload[val] = selectedItems
          .map((item) => {
            if (item?.value) return item?.value;
            else return item;
          })
          .flat();
      });
      if (sectionName) {
        dispatch(setFiltersPayload({ sectionName, data: payload }));
      }
    }
    if (theme.isLargeScreen) onClose();
    else navigation.goBack();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 2000);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  const parseSearchTerms = (query: string): string[] => {
    if (!query) return [];
    return query
      .split(",")
      .map((term) => term.trim().toLowerCase())
      .filter((term) => term.length > 0);
  };

  const matchesSearchTerms = (
    itemText: string,
    searchTerms: string[]
  ): boolean => {
    const itemLower = itemText.toLowerCase();
    return searchTerms.some((term) => itemLower.includes(term));
  };

  const mergedFilterData = useMemo(() => {
    if (!Array.isArray(options)) return [];

    let newFilteredData: any[];

    if (isEnableBrandSearch) {
      const searchTerms = parseSearchTerms(searchQuery);
      if (searchTerms.length === 0) {
        newFilteredData = [];
      } else {
        const exactMatches: any[] = [];
        const typedValuesNotInData: string[] = [];
        const addedValues = new Set<string>();

        searchTerms.forEach((term) => {
          const exactMatch = options.find((item) => {
            const itemText = item?.label || item?.value || item;
            const itemTextStr =
              typeof itemText === "string" ? itemText : String(itemText);
            return itemTextStr.toLowerCase() === term;
          });

          if (exactMatch) {
            const matchValue =
              exactMatch?.label || exactMatch?.value || exactMatch;
            const matchValueStr =
              typeof matchValue === "string" ? matchValue : String(matchValue);
            if (!addedValues.has(matchValueStr.toLowerCase())) {
              exactMatches.push(exactMatch);
              addedValues.add(matchValueStr.toLowerCase());
            }
          } else {
            if (!addedValues.has(term)) {
              typedValuesNotInData.push(term);
              addedValues.add(term);
            }
          }
        });

        const partialMatches = options.filter((item) => {
          const searchText = item?.label || item?.value || item;
          const searchTextStr =
            typeof searchText === "string" ? searchText : String(searchText);
          if (addedValues.has(searchTextStr.toLowerCase())) return false;
          return matchesSearchTerms(searchTextStr, searchTerms);
        });

        newFilteredData = [
          ...exactMatches,
          ...typedValuesNotInData,
          ...partialMatches,
        ];
      }
    } else {
      newFilteredData = options.filter((item) => {
        const searchText = item?.label || item?.value || item;
        const searchTextStr =
          typeof searchText === "string" ? searchText : String(searchText);
        return searchTextStr
          ?.toLowerCase()
          ?.includes(searchQuery.toLowerCase());
      });
    }

    if (searchQuery === "") {
      const newData = selectedItems.filter((item) => {
        if (!newFilteredData.includes(item)) {
          const searchText = item?.label || item?.value || item;
          const searchTextStr =
            typeof searchText === "string" ? searchText : String(searchText);
          return searchTextStr
            ?.toLowerCase()
            ?.includes(searchQuery.toLowerCase());
        }
        return false;
      });
      return [...newFilteredData, ...newData];
    }

    return newFilteredData;
  }, [options, searchQuery, selectedItems, isEnableBrandSearch]);

  const flatListData = useMemo(() => {
    if (!searchQuery && defaultList?.length > 0) {
      const newData = selectedItems.filter(
        (item) => !defaultList.includes(item)
      );
      return [...defaultList, ...newData];
    }
    return searchQuery || !server_side_search
      ? mergedFilterData
      : selectedItems;
  }, [
    searchQuery,
    server_side_search,
    mergedFilterData,
    selectedItems,
    defaultList,
  ]);

  const handleItemSelection = useCallback(
    (item: any, index: number) => {
      if (selectionType === SelectionType.single) {
        setSelectedItems((prev) => (prev?.includes(item) ? [] : [item]));
      } else if (selectionType === SelectionType.multiple) {
        setSelectedItems((prevSelected) =>
          prevSelected?.includes(item)
            ? prevSelected.filter((prevItem) => prevItem !== item)
            : [...prevSelected, item]
        );
      } else if (selectionType === SelectionType.range) {
        if (startRangeIndex === null) {
          setStartRangeIndex(index);
          setSelectedItems([item]);
        } else {
          const minIndex = Math.min(startRangeIndex, index);
          const maxIndex = Math.max(startRangeIndex, index);
          setSelectedItems(options?.slice(minIndex, maxIndex + 1) as []);
          setStartRangeIndex(null);
        }
      }
    },
    [selectionType, startRangeIndex, options]
  );

  const labelColorSelected = theme["core-05"];
  const labelColorUnselected = theme["core-06"];
  const bodyTextStyle = typography["Body-R"];

  const renderFilterListItem = useCallback(
    ({ item, index }: { item: any; index: number }) => (
      <FilterSearchCheckRow
        item={item}
        index={index}
        isChecked={
          !!(
            selectedItems?.includes(item?.value) ||
            selectedItems?.includes(item)
          )
        }
        labelColor={
          selectedItems?.includes(item)
            ? labelColorSelected
            : labelColorUnselected
        }
        textStyle={bodyTextStyle}
        onToggle={handleItemSelection}
      />
    ),
    [
      selectedItems,
      labelColorSelected,
      labelColorUnselected,
      bodyTextStyle,
      handleItemSelection,
    ]
  );

  const isDisabled = useCallback(() => {
    // return !selectedItems || selectedItems.length === 0;
    return false;
  }, [selectedItems]);

  const renderBottomButtons = () => {
    return (
      <ButtonSection
        paddingBottom={bottom}
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height;
          if (h > 0) setFooterBarHeight(h);
        }}
      >
        <ButtonWrapper>
          <FiltersButton
            isApplyButton={false}
            selectedStyle={selectedStyle}
            disabled={isDisabled()}
            onPress={() => {
              if (!isDisabled()) {
                resetAllFilters();
              }
            }}
          >
            <VDBText
              type={typography["H3-M"]}
              color={
                isDisabled()
                  ? theme["primary-cont-disabled"]
                  : (theme[
                      selectedTheme.secondary as keyof DefaultTheme
                    ] as string)
              }
            >
              {t("searchResult.web.screenOptions.reset.text")}
            </VDBText>
          </FiltersButton>

          <FiltersButton
            onPress={() => {
              if (!isDisabled()) {
                applyFilters();
              }
            }}
            isApplyButton={true}
            selectedStyle={selectedStyle}
            disabled={isDisabled()}
          >
            <VDBText
              type={typography["H3-M"]}
              color={
                isDisabled() ? theme["primary-cont-disabled"] : theme["diam-05"]
              }
            >
              {t("searchResult.web.screenOptions.done.text")}
            </VDBText>
          </FiltersButton>
        </ButtonWrapper>
      </ButtonSection>
    );
  };

  const handleValuesSelection = (item: any, checked: boolean) => {
    if (!item?.payload_name) return;

    if (
      selectedItems.findIndex(
        (i) => i.payload_name?.[0] === item.payload_name?.[0]
      ) !== -1
    ) {
      setSelectedSubItems([]);
      dispatch(
        removeFiltersPayload({
          sectionName,
          payloadName: [item?.selection_key],
        })
      );
    } else {
      const payload = {
        [item?.payload_name?.[0]]: checked,
      };
      setSelectedSubItems([item]);
      dispatch(setFiltersPayload({ sectionName, data: payload }));
    }
  };

  const handleEnterPress = () => {
    if (
      !isEnableBrandSearch ||
      !mergedFilterData ||
      mergedFilterData.length === 0
    )
      return;

    const newItemsFromFiltered = mergedFilterData
      .map((item) => item?.value || item)
      .filter((item) => !selectedItems.includes(item))
      .slice(0, 1);

    const itemsToSelect = [...selectedItems, ...newItemsFromFiltered];
    setSelectedItems(itemsToSelect);

    setTimeout(() => {
      if (itemsToSelect.length === 0 && sectionName && payloadName) {
        dispatch(removeFiltersPayload({ sectionName, payloadName }));
      } else {
        const payload: any = {};
        payloadName?.forEach((val: string) => {
          payload[val] = itemsToSelect
            .map((item) => {
              if (item?.value) return item?.value;
              else return item;
            })
            .flat();
        });
        if (sectionName) {
          dispatch(setFiltersPayload({ sectionName, data: payload }));
        }
      }
      if (theme.isLargeScreen) onClose();
      else navigation.goBack();
    }, 0);
  };

  return (
    <>
      <StyledLayoutContainer backgroundColor="core-01">
        <ListContainer
          enableBackground={
            (flatListData?.length ?? 0) > 0 ||
            (selectedSubItems?.length ?? 0) > 0
          }
        >
          <SearchContainer>
            <Controller
              control={control}
              name="searchQueryFormValue"
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  inputRef={inputRef}
                  leftIcon={customIcon ? customIcon : ""}
                  rightIcon={VDBIcons.MAIN_SEARCH}
                  inputLabel={t(inputLabel ?? "") ?? ""}
                  autoFocus={false}
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(text);
                    setSearchQuery(text);
                  }}
                  value={value}
                  {...(isEnableBrandSearch && {
                    onSubmitEditing: () => {
                      handleEnterPress();
                    },
                  })}
                />
              )}
            />
            {sub_options?.[0]?.options?.length > 0 && (
              <SubOptionsContainer>
                {sub_options?.[0]?.options?.map((item: any) => (
                  <CheckBox
                    key={item.value}
                    text={item.label}
                    isChecked={selectedSubItems?.includes(item)}
                    onPress={(checked) => handleValuesSelection(item, checked)}
                  />
                ))}
              </SubOptionsContainer>
            )}
            {isFetching && (
              <ActivityContainer>
                <ActivityIndicator
                  color={
                    (selectedStyle?.primary
                      ? (theme[
                          selectedStyle.primary as keyof DefaultTheme
                        ] as string)
                      : theme["core-05"]) || theme["core-05"]
                  }
                />
              </ActivityContainer>
            )}
          </SearchContainer>
          <FlatList
            style={flatListFlexStyle}
            keyExtractor={(item: any, index: number) =>
              item?.value?.toString() || item?.toString() || `item-${index}`
            }
            data={flatListData}
            extraData={selectedItems}
            renderItem={renderFilterListItem}
            contentContainerStyle={{
              marginLeft: SpaceType.Double,
              marginRight: SpaceType.Double,
              marginTop: SpaceType.Basic,
              paddingBottom: footerBarHeight,
              flexGrow: 1,
            }}
            keyboardShouldPersistTaps="handled"
            ItemSeparatorComponent={FilterSearchListSeparator}
            removeClippedSubviews={Platform.OS === "android"}
            windowSize={11}
            maxToRenderPerBatch={12}
            updateCellsBatchingPeriod={50}
          />
        </ListContainer>
        {theme.isLargeScreen ? (
          <Portal hostName={"searchFilter"}>{renderBottomButtons()}</Portal>
        ) : (
          renderBottomButtons()
        )}
      </StyledLayoutContainer>
    </>
  );
};

type FilterSearchCheckRowProps = {
  item: any;
  index: number;
  isChecked: boolean;
  labelColor: string;
  textStyle: any;
  onToggle: (payload: any, index: number) => void;
};

const FilterSearchCheckRow = React.memo(function FilterSearchCheckRow({
  item,
  index,
  isChecked,
  labelColor,
  textStyle,
  onToggle,
}: FilterSearchCheckRowProps) {
  const displayText = item?.label?.toString() || item?.toString() || "";
  return (
    <CheckBoxDisplay>
      <CheckBox
        text={displayText}
        labelColor={labelColor}
        isChecked={isChecked}
        styles={textStyle}
        onPress={() => onToggle(item.value || item, index)}
      />
    </CheckBoxDisplay>
  );
});

const StyledLayoutContainer = styled(LayoutContainer)`
  background-color: ${({ theme }) => theme["core-01"]};
  height: 100%;
  flex-direction: column;
  flex: 1;
`;

const ButtonSection = styled.View<{ paddingBottom: number }>`
  flex-direction: row;
  justify-content: center;
  background-color: ${({ theme }) => theme["core-01"]};
  flex: 1;
  border-bottom-left-radius: ${SpaceType.Basic}px;
  border-bottom-right-radius: ${SpaceType.Basic}px;
  gap: ${SpaceType.Double}px;
  padding: ${SpaceType.Double}px 0;
  padding-top: ${SpaceType.large}px;
  padding-bottom: ${({ paddingBottom, theme }) =>
    ((theme.isLargeScreen || theme.isTablet) && Platform.OS === "android"
      ? SpaceType.large
      : SpaceType.large) + paddingBottom}px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`;

const FiltersButton = styled.TouchableOpacity<{
  isApplyButton: boolean;
  selectedStyle: SelectedItemTheme;
  disabled: boolean;
}>`
  width: 100%;
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 40px;
  border-radius: ${SpaceType.Basic}px;
  background-color: ${({ theme, isApplyButton, selectedStyle, disabled }) =>
    disabled
      ? theme["primary-bg-disabled"]
      : isApplyButton
      ? (theme[selectedStyle?.one as keyof DefaultTheme] as string)
      : (theme[selectedStyle?.three as keyof DefaultTheme] as string)};
`;

const ButtonWrapper = styled.View`
  flex-direction: row;
  gap: ${SpaceType.Double};
  width: 100%;
  max-width: ${({ theme }) => (theme.isLargeScreen ? 1072 : 705)}px;
  align-items: center;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

const CheckBoxDisplay = styled.View`
  flex-direction: row;
  align-items: center;
  margin-top: ${SpaceType.Double}px;
  margin-bottom: ${SpaceType.Double}px;
  gap: ${SpaceType.Basic}px;
`;

const Separator = styled.View`
  height: 1px;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
`;

const FilterSearchListSeparator = () => <Separator />;

const ActivityContainer = styled.View`
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const SearchContainer = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  margin-top: ${SpaceType.Big}px;
  width: 100%;
  max-width: ${({ theme }) => (theme.isLargeScreen ? 1072 : 705)}px;
  margin-left: auto;
  margin-right: auto;
`;

const ListContainer = styled.View<{ enableBackground: boolean }>`
  width: 100%;
  max-width: ${({ theme }) => (theme.isLargeScreen ? 1072 : 705)}px;
  margin-left: auto;
  margin-right: auto;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  ${({ enableBackground }) =>
    enableBackground &&
    css`
      background-color: ${({ theme }) => theme["core-02"]};
    `}
`;

const SubOptionsContainer = styled.View`
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.Big}px;
`;

export default FilterSearchScreen;
